/**
 * Answer what Claude asks, in place (R-HL-6, ADR 0056): AskUserQuestion questions, plan
 * approvals (ExitPlanMode) and MCP forms. One component per kind, laid out from the same parts
 * on three surfaces: the Inbox's side panel (body above, decision bar below), the session
 * drawer's card and the phone card (`AskCard`). The answer goes through the approvals decide
 * call; the moment a person answers the card collapses to "You answered: ..." with its delivery
 * (the held hook returning is the delivery), and an error from the hub comes back inline.
 *
 * Keyboard: number keys pick an option of the question in focus (a plan: its mode), Ctrl+Enter
 * (Cmd+Enter) sends. In the Inbox the keys work page-wide; elsewhere inside the card.
 */
import { Ban, Check, CircleCheck, ClipboardList, CornerUpLeft, Ellipsis, ExternalLink, FileText, Globe, Hand, MessageCircleQuestion, SendHorizontal, TextCursorInput, Undo2 } from '@orgus/ui/icons';
import type { LucideIcon } from '@orgus/ui/icons';
import { useEffect, useId, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import type { ApprovalDecision, ApprovalRequest, PlanContinueMode } from '@orgus/protocol';
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  IconButton,
  Input,
  Kbd,
  Markdown,
  RadioGroup,
  RadioGroupItem,
  Segmented,
  Select,
  Spinner,
  Textarea,
  Tooltip,
  cn,
  formatClock,
  formatDuration,
  formatRelative,
  isTypingTarget,
  toUiError,
  toast,
  useNow,
} from '@orgus/ui';
import { useCan, useMe } from '../auth/useMe.ts';
import { useOrgus } from '../../lib/store.ts';
import { handBackApproval } from '../asks/api.ts';
import { useSessionTitle } from '../sessions/title.tsx';
import { decideApproval } from './live.ts';
import {
  PLAN_MODES,
  answerLine,
  answerProblem,
  askKindOf,
  askOf,
  askSummary,
  denyDecision,
  elicitationContent,
  elicitationDecision,
  elicitationFields,
  fallbackLine,
  fieldError,
  firstLine,
  givenOf,
  initialValues,
  isAnswered,
  keepPlanningDecision,
  missingText,
  nextUnanswered,
  pickOption,
  pickOther,
  planApproveDecision,
  planBody,
  questionAnswers,
  questionAnswersOf,
  questionsDecision,
  safeLink,
} from './askModel.ts';
import type { AskKind, ElicitationAsk, FieldView, FormValues, GivenAnswer, PlanAsk, QuestionPicks, QuestionsAsk } from './askModel.ts';
import { otherOverlayOpen } from './keys.ts';

/** inbox: the Inbox's side panel. drawer: the session drawer's card. phone: the phone card (44 px controls). */
export type AskSurface = 'inbox' | 'drawer' | 'phone';

export interface AskParts {
  body: ReactNode;
  footer: ReactNode;
}

const OTHER = '__other__';

const KIND_ICON: Record<AskKind, LucideIcon> = {
  question: MessageCircleQuestion,
  plan: ClipboardList,
  elicitation: TextCursorInput,
};

/** "You" for the signed-in person (and the demo's owner), else their name. */
export function useAnswerer(): (by: string | undefined) => string {
  const me = useMe((s) => s.me?.actor.id ?? null);
  const mock = useOrgus((s) => s.mode === 'mock');
  return (by) => (!by || by === me || by === 'you' || ((mock || !me) && by === 'owner') ? 'You' : by);
}

/** "4m left", quiet until the last 3 minutes; when it was asked and what happens after, in the tooltip. */
export function TimeLeft({ a, now }: { a: Pick<ApprovalRequest, 'createdAt' | 'expiresAt' | 'fallback'>; now: number }) {
  const left = a.expiresAt - now;
  const urgent = left < 3 * 60_000;
  const after = a.fallback === 'session' ? 'Then Claude asks in the session.' : `Declined at ${formatClock(a.expiresAt)} with no answer.`;
  return (
    <Tooltip content={`Asked at ${formatClock(a.createdAt)}. ${after}`}>
      <span tabIndex={0} className={cn('shrink-0 text-caption num', urgent ? 'font-medium text-status-attention' : 'text-fg-subtle')}>
        {left > 0 ? `${formatDuration(left).split(' ')[0]} left` : 'Expiring'}
      </span>
    </Tooltip>
  );
}

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

type SendPhase = { kind: 'idle' } | { kind: 'sending'; given: GivenAnswer } | { kind: 'sent'; given: GivenAnswer; at: number };

function useAskSend(a: ApprovalRequest, surface: AskSurface) {
  const canAnswer = useCan('operator');
  const [phase, setPhase] = useState<SendPhase>({ kind: 'idle' });
  const [error, setError] = useState<string | null>(null);
  const send = async (d: ApprovalDecision) => {
    const ask = askOf(a);
    const problem = ask ? answerProblem(ask, d) : null;
    if (problem) return setError(problem);
    setError(null);
    const given: GivenAnswer = {
      answer: d.answer,
      ...(d.note ? { note: d.note } : {}),
      ...(d.ask ? { ask: d.ask } : {}),
    };
    setPhase({ kind: 'sending', given });
    try {
      const done = await decideApproval(a.id, d);
      setPhase({ kind: 'sent', given, at: done?.decidedAt ?? Date.now() });
      // The Inbox moves on to the next item: the receipt goes with a toast (success only, never an error).
      if (surface === 'inbox' && ask)
        toast(answerLine(ask, given), {
          tone: 'success',
          description: 'Delivered to the session.',
        });
    } catch (e) {
      setPhase({ kind: 'idle' });
      const err = toUiError(e);
      setError([err.what, err.why, err.next].filter(Boolean).join(' '));
    }
  };
  return {
    send,
    phase,
    error,
    setError,
    canAnswer,
    busy: phase.kind !== 'idle',
  };
}

type Send = ReturnType<typeof useAskSend>;

/** Keys for an ask: page-wide in the Inbox (`global`), else on the card's own onKeyDown. */
function useAskKeys(global: boolean, on: boolean, handle: (e: KeyboardEvent | ReactKeyboardEvent) => boolean) {
  const ref = useRef(handle);
  ref.current = handle;
  useEffect(() => {
    if (!global || !on) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing) return;
      // Never inside another dialog, menu or list (the bulk dialogs, a confirm, the session drawer on top).
      if (otherOverlayOpen()) return;
      if (ref.current(e)) e.preventDefault();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [global, on]);
  return (e: ReactKeyboardEvent) => {
    if (global || !on || e.defaultPrevented) return;
    if (ref.current(e)) e.preventDefault();
  };
}

const isSendKey = (e: KeyboardEvent | ReactKeyboardEvent) => e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !e.altKey;
/** A digit with no modifier, outside a text field: 1 to 9, as an index, else null. */
function digitOf(e: KeyboardEvent | ReactKeyboardEvent): number | null {
  if (e.ctrlKey || e.metaKey || e.altKey || isTypingTarget(e.target)) return null;
  return /^[1-9]$/.test(e.key) ? Number(e.key) - 1 : null;
}

function ErrorLine({ text }: { text?: string | null }) {
  if (!text) return null;
  return (
    <p role="alert" className="rounded-control bg-status-danger-soft px-3 py-2 text-ui text-fg">
      {text}
    </p>
  );
}

/** "You answered: ..." with its delivery: Sending, then Delivered to the session with the time. */
export function AskReceipt({ line, sending, at }: { line: string; sending: boolean; at?: number }) {
  return (
    <div role="status" className="motion-arrive flex min-w-0 items-start gap-3">
      <span className="flex h-5 shrink-0 items-center">{sending ? <Spinner size={16} label="Sending" /> : <CircleCheck className="size-4 text-status-success" aria-hidden />}</span>
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="line-clamp-3 text-ui font-medium text-fg wrap-anywhere">{line}</span>
        <span className="text-caption text-fg-subtle num">{sending ? 'Sending to the session' : `Delivered to the session${at ? `, ${formatClock(at)}` : ''}`}</span>
      </span>
    </div>
  );
}

const btn = (surface: AskSurface) => (surface === 'phone' ? ({ size: 'lg', className: 'min-h-11' } as const) : ({ size: 'sm' } as const));

/** The decision bar: the error above, the buttons in one row (stacked full width on the phone). */
function Actions({ surface, s, more, children }: { surface: AskSurface; s: Send; more?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex w-full flex-col gap-3">
      <ErrorLine text={s.error} />
      {!s.canAnswer && <p className="text-ui text-fg-muted">Only an operator or the owner can answer.</p>}
      <div className={cn('flex w-full items-center gap-2', surface === 'phone' ? 'flex-col-reverse items-stretch' : 'flex-wrap')}>
        {more}
        {surface !== 'phone' && <span className="mr-auto" />}
        {children}
      </div>
    </div>
  );
}

/** The overflow: skip a question, hand a held ask back to the session's own dialog. Errors inline. */
function MoreMenu({ a, s, surface, onSkip }: { a: ApprovalRequest; s: Send; surface: AskSurface; onSkip?: () => void }) {
  const [busy, setBusy] = useState(false);
  if (a.fallback !== 'session' && !onSkip) return null;
  const handBack = async () => {
    setBusy(true);
    s.setError(null);
    try {
      await handBackApproval(a.id);
    } catch (e) {
      const err = toUiError(e);
      s.setError([err.what, err.why, err.next].filter(Boolean).join(' '));
    } finally {
      setBusy(false);
    }
  };
  const items = (
    <DropdownMenuContent align="start" className="w-64">
      {onSkip && (
        <DropdownMenuItem icon={<Ban />} onSelect={onSkip}>
          Skip question
        </DropdownMenuItem>
      )}
      {a.fallback === 'session' && (
        <DropdownMenuItem icon={<CornerUpLeft />} onSelect={() => void handBack()}>
          Hand back to the session
        </DropdownMenuItem>
      )}
    </DropdownMenuContent>
  );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {surface === 'phone' ? (
          <Button {...btn(surface)} variant="ghost" icon={<Ellipsis />} loading={busy} disabled={s.busy || !s.canAnswer}>
            More answers
          </Button>
        ) : (
          <IconButton size="sm" label="More answers" icon={<Ellipsis />} loading={busy} disabled={s.busy || !s.canAnswer} />
        )}
      </DropdownMenuTrigger>
      {items}
    </DropdownMenu>
  );
}

/** A short note composer: skip a question with a note, keep planning with feedback. */
function NoteForm({
  surface,
  label,
  placeholder,
  required,
  submitLabel,
  submitIcon,
  s,
  onCancel,
  onSubmit,
}: {
  surface: AskSurface;
  label: string;
  placeholder: string;
  required?: boolean;
  submitLabel: string;
  submitIcon: ReactNode;
  s: Send;
  onCancel: () => void;
  onSubmit: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  const [missing, setMissing] = useState(false);
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => ref.current?.focus(), []);
  const submit = () => {
    if (required && !note.trim()) {
      setMissing(true);
      ref.current?.focus();
      return;
    }
    onSubmit(note);
  };
  return (
    <form
      className="flex w-full flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <Textarea
        ref={ref}
        label={label}
        rows={surface === 'inbox' ? 2 : 3}
        value={note}
        placeholder={placeholder}
        maxLength={2000}
        error={missing && !note.trim() ? 'Say what Claude should change.' : undefined}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onCancel();
          }
          if (isSendKey(e)) {
            e.preventDefault();
            e.stopPropagation();
            submit();
          }
        }}
      />
      <ErrorLine text={s.error} />
      <div className={cn('flex gap-2', surface === 'phone' ? 'flex-col-reverse' : 'justify-end')}>
        <Button {...btn(surface)} variant="ghost" icon={<Undo2 />} onClick={onCancel}>
          Cancel
        </Button>
        <Button {...btn(surface)} variant="primary" type="submit" icon={submitIcon} loading={s.busy} shortcut={surface === 'phone' ? undefined : ['mod', 'enter']}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// Questions
// ---------------------------------------------------------------------------

function OptionRow({ id, control, label, description, keyHint, on, phone, children }: { id: string; control: ReactNode; label: string; description?: string; keyHint?: string; on: boolean; phone: boolean; children?: ReactNode }) {
  return (
    <div className={cn('flex flex-col rounded-control transition-colors duration-(--duration-fast)', on ? 'selected-tint' : 'hover:bg-hover')}>
      <label htmlFor={id} className={cn('flex cursor-pointer items-start gap-3 px-3', phone ? 'min-h-11 py-3' : 'py-2')}>
        <span className="flex h-5 shrink-0 items-center">{control}</span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="text-ui font-medium text-fg">{label}</span>
          {description && <span className="text-caption text-fg-subtle">{description}</span>}
        </span>
        {keyHint && <Kbd keys={[keyHint]} className="pointer-coarse:hidden" />}
      </label>
      {children && (
        <div className="flex gap-3 px-3 pb-3">
          <span className="w-4 shrink-0" aria-hidden />
          <div className="min-w-0 flex-1">{children}</div>
        </div>
      )}
    </div>
  );
}

function QuestionsAnswer({ a, ask, surface, keys, s, render }: { a: ApprovalRequest; ask: QuestionsAsk; surface: AskSurface; keys: boolean; s: Send; render: (p: AskParts) => ReactNode }) {
  const qs = ask.questions;
  const uid = useId();
  const [picks, setPicks] = useState<QuestionPicks>({});
  const [active, setActive] = useState(0);
  const [skipping, setSkipping] = useState(false);
  const otherRefs = useRef<(HTMLInputElement | null)[]>([]);
  const focusOther = useRef<number | null>(null);
  const disabled = s.busy || !s.canAnswer;
  const phone = surface === 'phone';

  useEffect(() => {
    if (focusOther.current != null) {
      otherRefs.current[focusOther.current]?.focus();
      focusOther.current = null;
    }
  });

  const update = (i: number, next: QuestionPicks[number], advance: boolean) => {
    const all = { ...picks, [i]: next };
    setPicks(all);
    s.setError(null);
    if (advance && qs.length > 1) setActive(nextUnanswered(qs.length, all, i) ?? i);
  };
  const choose = (i: number, label: string) => update(i, pickOption(qs[i]!, picks[i], label), !qs[i]!.multiSelect);
  const chooseOther = (i: number, on: boolean) => {
    if (on) focusOther.current = i;
    setActive(i);
    update(i, pickOther(qs[i]!, picks[i], on), false);
  };
  const submit = () => {
    if (disabled) return;
    const { answers, missing } = questionAnswers(qs, picks);
    if (missing.length) {
      setActive(missing[0]!);
      s.setError(missingText(qs, missing));
      return;
    }
    void s.send(questionsDecision(answers));
  };

  const onKey = useAskKeys(keys, !disabled && !skipping, (e) => {
    if (isSendKey(e)) return (submit(), true);
    const d = digitOf(e);
    if (d == null) return false;
    const q = qs[active]!;
    if (d < q.options.length) choose(active, q.options[d]!.label);
    else if (d === q.options.length) chooseOther(active, q.multiSelect ? picks[active]?.other == null : true);
    else return false;
    return true;
  });

  const question = (q: QuestionsAsk['questions'][number], qi: number) => {
    const p = picks[qi];
    const otherOn = p?.other != null;
    // Key hints where the keys work page-wide (the Inbox); in a card they work once focus is in it.
    const hint = (i: number) => (keys && qi === active ? String(i + 1) : undefined);
    const otherField = otherOn && (
      <Input
        ref={(el) => {
          otherRefs.current[qi] = el;
        }}
        size={phone ? 'md' : 'sm'}
        aria-label={`Your own answer to: ${q.question}`}
        placeholder="Type your answer"
        value={p?.other ?? ''}
        maxLength={2000}
        disabled={disabled}
        onChange={(e) => update(qi, pickOther(q, p, true, e.target.value), false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
    );
    const otherId = `${uid}-${qi}-other`;
    const options = q.multiSelect ? (
      <div role="group" aria-label={q.question} className="flex flex-col gap-1">
        {q.options.map((o, i) => {
          const id = `${uid}-${qi}-${i}`;
          const on = !!p?.labels.includes(o.label);
          return (
            <OptionRow key={o.label} id={id} on={on} label={o.label} description={o.description} keyHint={hint(i)} phone={phone} control={<Checkbox id={id} checked={on} disabled={disabled} onCheckedChange={() => choose(qi, o.label)} />} />
          );
        })}
        <OptionRow id={otherId} on={otherOn} label="Other" keyHint={hint(q.options.length)} phone={phone} control={<Checkbox id={otherId} checked={otherOn} disabled={disabled} onCheckedChange={(c) => chooseOther(qi, c)} />}>
          {otherField}
        </OptionRow>
      </div>
    ) : (
      <RadioGroup aria-label={q.question} value={otherOn ? OTHER : (p?.labels[0] ?? '')} onValueChange={(v) => (v === OTHER ? chooseOther(qi, true) : choose(qi, v))} disabled={disabled} className="flex flex-col gap-1">
        {q.options.map((o, i) => {
          const id = `${uid}-${qi}-${i}`;
          return <OptionRow key={o.label} id={id} on={p?.labels[0] === o.label && !otherOn} label={o.label} description={o.description} keyHint={hint(i)} phone={phone} control={<RadioGroupItem id={id} value={o.label} />} />;
        })}
        <OptionRow id={otherId} on={otherOn} label="Other" keyHint={hint(q.options.length)} phone={phone} control={<RadioGroupItem id={otherId} value={OTHER} />}>
          {otherField}
        </OptionRow>
      </RadioGroup>
    );
    return (
      <section key={qi} aria-label={q.header?.trim() || `Question ${qi + 1}`} className="flex flex-col gap-3" onFocusCapture={() => setActive(qi)}>
        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-2">
            {q.header?.trim() && (
              <span className="flex h-5 shrink-0 items-center">
                <Badge>{q.header.trim()}</Badge>
              </span>
            )}
            <p className="min-w-0 flex-1 whitespace-pre-wrap text-body font-medium text-fg">{q.question}</p>
            {qs.length > 1 && isAnswered(p) && (
              <span className="flex h-5 shrink-0 items-center">
                <Check className="size-4 text-fg-subtle" aria-label="Answered" />
              </span>
            )}
          </div>
          {q.multiSelect && <p className="text-caption text-fg-subtle">Pick any that apply</p>}
        </div>
        {options}
      </section>
    );
  };

  const body = (
    <div className="flex flex-col gap-6" onKeyDown={onKey}>
      {qs.slice(0, 4).map(question)}
    </div>
  );

  const footer = skipping ? (
    <NoteForm
      surface={surface}
      label="Note for Claude"
      placeholder="Go with your best judgement"
      submitLabel="Skip question"
      submitIcon={<Ban />}
      s={s}
      onCancel={() => setSkipping(false)}
      onSubmit={(note) => void s.send(denyDecision(note))}
    />
  ) : (
    <Actions surface={surface} s={s} more={<MoreMenu a={a} s={s} surface={surface} onSkip={() => setSkipping(true)} />}>
      <Button {...btn(surface)} variant="primary" icon={<SendHorizontal />} shortcut={phone ? undefined : ['mod', 'enter']} disabled={!s.canAnswer} loading={s.busy} onClick={submit}>
        Send answer
      </Button>
    </Actions>
  );
  return <>{render({ body, footer })}</>;
}

// ---------------------------------------------------------------------------
// Plan
// ---------------------------------------------------------------------------

function PlanAnswer({ a, ask, surface, keys, s, render }: { a: ApprovalRequest; ask: PlanAsk; surface: AskSurface; keys: boolean; s: Send; render: (p: AskParts) => ReactNode }) {
  const [mode, setMode] = useState<PlanContinueMode>('default');
  const [keepPlanning, setKeepPlanning] = useState(false);
  const disabled = s.busy || !s.canAnswer;
  const approve = () => !disabled && void s.send(planApproveDecision(mode));
  const phone = surface === 'phone';

  const onKey = useAskKeys(keys, !disabled && !keepPlanning, (e) => {
    if (isSendKey(e)) return (approve(), true);
    const d = digitOf(e);
    if (d == null || d >= PLAN_MODES.length) return false;
    setMode(PLAN_MODES[d]!.value);
    return true;
  });

  const body = (
    <div className="flex flex-col gap-3" onKeyDown={onKey}>
      <article aria-label="Plan" tabIndex={0} className={cn('rounded-card bg-sunken px-6 py-4 focus-visible:outline-2 focus-visible:outline-accent', surface !== 'inbox' && 'max-h-96 overflow-y-auto', phone && 'px-4')}>
        <Markdown source={surface === 'inbox' ? planBody(ask.plan) || ask.plan : ask.plan} size={phone ? 'sm' : 'md'} />
      </article>
      {ask.planFilePath && (
        <p className="flex min-w-0 items-center gap-2 text-caption text-fg-subtle" title={ask.planFilePath}>
          <FileText className="size-3 shrink-0" aria-hidden />
          <span className="truncate font-mono">{ask.planFilePath}</span>
        </p>
      )}
      {!keepPlanning && (
        <div className={cn('flex gap-3 pt-1', phone ? 'flex-col' : 'items-center')}>
          <span className="text-ui text-fg-muted">After approval</span>
          <Segmented label="After approval" size={phone ? 'md' : 'sm'} value={mode} onChange={setMode} options={PLAN_MODES} className={cn(phone && 'w-full')} />
        </div>
      )}
    </div>
  );

  const footer = keepPlanning ? (
    <NoteForm
      surface={surface}
      label="What should Claude change"
      placeholder="Split the migration into two steps"
      required
      submitLabel="Send feedback"
      submitIcon={<SendHorizontal />}
      s={s}
      onCancel={() => setKeepPlanning(false)}
      onSubmit={(note) => {
        const d = keepPlanningDecision(note);
        if (d) void s.send(d);
      }}
    />
  ) : (
    <Actions surface={surface} s={s} more={<MoreMenu a={a} s={s} surface={surface} />}>
      <Button {...btn(surface)} variant="secondary" icon={<Undo2 />} disabled={disabled} onClick={() => setKeepPlanning(true)}>
        Keep planning
      </Button>
      <Button {...btn(surface)} variant="primary" icon={<Check />} shortcut={phone ? undefined : ['mod', 'enter']} disabled={!s.canAnswer} loading={s.busy} onClick={approve}>
        Approve plan
      </Button>
    </Actions>
  );
  return <>{render({ body, footer })}</>;
}

// ---------------------------------------------------------------------------
// MCP form
// ---------------------------------------------------------------------------

function FieldLabel({ f }: { f: FieldView }) {
  return (
    <>
      {f.label}
      {f.required && (
        <span className="text-fg-subtle" aria-hidden>
          {' '}
          *
        </span>
      )}
    </>
  );
}

function FormField({ f, value, error, disabled, phone, onChange, onBlur }: { f: FieldView; value: string | boolean; error?: string; disabled: boolean; phone: boolean; onChange: (v: string | boolean) => void; onBlur: () => void }) {
  const { minimum: lo, maximum: hi } = f.field;
  const range = lo != null && hi != null ? `Between ${String(lo)} and ${String(hi)}.` : lo != null ? `At least ${String(lo)}.` : hi != null ? `At most ${String(hi)}.` : null;
  const hint = [f.field.description ? firstLine(f.field.description, 120) : null, range].filter(Boolean).join(' ') || undefined;
  if (f.control === 'checkbox') {
    return <Checkbox name={f.name} checked={value === true} disabled={disabled} label={f.label} hint={hint} onCheckedChange={(c) => onChange(c)} />;
  }
  if (f.control === 'select') {
    return (
      <Select
        name={f.name}
        label={<FieldLabel f={f} />}
        hint={hint}
        error={error}
        placeholder="Choose one"
        options={f.options!}
        value={typeof value === 'string' ? value : ''}
        disabled={disabled}
        required={f.required}
        onValueChange={(v) => onChange(v)}
      />
    );
  }
  if (f.control === 'radio') {
    return (
      <Field label={<FieldLabel f={f} />} hint={hint} error={error}>
        {({ id, describedBy, invalid }) => (
          <RadioGroup
            id={id}
            name={f.name}
            aria-describedby={describedBy}
            aria-invalid={invalid || undefined}
            aria-required={f.required || undefined}
            value={typeof value === 'string' ? value : ''}
            onValueChange={(v) => onChange(v)}
            disabled={disabled}
            className="flex flex-col gap-1"
          >
            {f.options!.map((o) => (
              <label key={o.value} className={cn('flex cursor-pointer items-center gap-3 text-ui text-fg', phone ? 'min-h-11' : 'min-h-control-sm')}>
                <RadioGroupItem value={o.value} />
                {o.label}
              </label>
            ))}
          </RadioGroup>
        )}
      </Field>
    );
  }
  const type = f.control === 'email' ? 'email' : f.control === 'url' ? 'url' : f.control === 'date' ? 'date' : f.control === 'datetime' ? 'datetime-local' : 'text';
  return (
    <Input
      name={f.name}
      label={<FieldLabel f={f} />}
      hint={hint}
      error={error}
      type={type}
      inputMode={f.control === 'number' ? 'decimal' : f.control === 'integer' ? 'numeric' : f.control === 'email' ? 'email' : f.control === 'url' ? 'url' : undefined}
      autoComplete={f.control === 'email' ? 'email' : 'off'}
      placeholder={f.control === 'email' ? 'name@example.com' : f.control === 'url' ? 'https://example.com' : undefined}
      required={f.required}
      maxLength={f.field.maxLength}
      value={typeof value === 'string' ? value : ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
    />
  );
}

function ElicitationAnswer({ a, ask, surface, keys, s, render }: { a: ApprovalRequest; ask: ElicitationAsk; surface: AskSurface; keys: boolean; s: Send; render: (p: AskParts) => ReactNode }) {
  const fields = elicitationFields(ask.schema);
  const formId = useId();
  const formRef = useRef<HTMLFormElement>(null);
  const [values, setValues] = useState<FormValues>(() => initialValues(fields));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const disabled = s.busy || !s.canAnswer;
  const phone = surface === 'phone';
  const link = ask.mode === 'url' ? safeLink(ask.url) : null;
  const decline = () => !disabled && void s.send(denyDecision());

  const submit = () => {
    if (disabled) return;
    if (ask.mode === 'url') return void s.send({ answer: 'allow', scope: 'once' });
    const { content, errors: errs } = elicitationContent(fields, values);
    setErrors(errs);
    const first = fields.find((f) => errs[f.name]);
    if (first) {
      formRef.current?.querySelector<HTMLElement>(`[name="${CSS.escape(first.name)}"]`)?.focus();
      return;
    }
    void s.send(elicitationDecision(content));
  };

  const onKey = useAskKeys(keys, !disabled, (e) => (isSendKey(e) ? (submit(), true) : false));

  const body = (
    <div className="flex flex-col gap-6" onKeyDown={onKey}>
      <p className="whitespace-pre-wrap text-body text-fg">{ask.message.trim()}</p>
      {ask.mode === 'url' ? (
        <div className={cn('flex gap-3 rounded-card bg-sunken px-4 py-3', phone ? 'flex-col' : 'items-center')}>
          <span className="flex min-w-0 flex-1 items-center gap-3">
            <Globe className="size-4 shrink-0 text-fg-subtle" aria-hidden />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-ui font-medium text-fg">{link ? link.host : 'No link given'}</span>
              {link && <span className="truncate font-mono text-caption text-fg-subtle">{link.href}</span>}
            </span>
          </span>
          {link && (
            <Button {...btn(surface)} variant="secondary" icon={<ExternalLink />} onClick={() => window.open(link.href, '_blank', 'noopener,noreferrer')}>
              Open link
            </Button>
          )}
        </div>
      ) : fields.length ? (
        <form
          id={formId}
          ref={formRef}
          noValidate
          className="flex max-w-(--layout-width-form) flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          {fields.map((f) => (
            <FormField
              key={f.name}
              f={f}
              phone={phone}
              value={values[f.name] ?? ''}
              error={errors[f.name]}
              disabled={disabled}
              onChange={(v) => {
                setValues((x) => ({ ...x, [f.name]: v }));
                if (errors[f.name] && (fieldError(f, v) == null || f.control === 'radio' || f.control === 'select')) setErrors(({ [f.name]: _, ...rest }) => rest);
              }}
              onBlur={() => {
                const err = fieldError(f, values[f.name]);
                setErrors((x) => {
                  const { [f.name]: _, ...rest } = x;
                  return err ? { ...rest, [f.name]: err } : rest;
                });
              }}
            />
          ))}
        </form>
      ) : null}
    </div>
  );

  const footer = (
    <Actions surface={surface} s={s} more={<MoreMenu a={a} s={s} surface={surface} />}>
      <Button {...btn(surface)} variant="secondary" icon={<Ban />} disabled={disabled} onClick={decline}>
        Decline
      </Button>
      <Button
        {...btn(surface)}
        variant="primary"
        icon={ask.mode === 'url' ? <Check /> : <SendHorizontal />}
        shortcut={phone ? undefined : ['mod', 'enter']}
        disabled={!s.canAnswer}
        loading={s.busy}
        {...(ask.mode === 'form' && fields.length ? { type: 'submit' as const, form: formId } : { onClick: submit })}
      >
        {ask.mode === 'url' ? 'Mark as done' : 'Send form'}
      </Button>
    </Actions>
  );
  return <>{render({ body, footer })}</>;
}

// ---------------------------------------------------------------------------
// The switch, the card and the record
// ---------------------------------------------------------------------------

export interface AskAnswerProps {
  approval: ApprovalRequest;
  surface: AskSurface;
  /** Page-wide keys (the Inbox's selected item). Elsewhere the keys work inside the card. */
  keys?: boolean;
  /** Lays the parts out; by default the body, then the actions. */
  render?: (p: AskParts) => ReactNode;
}

function stacked(p: AskParts): ReactNode {
  return (
    <div className="flex flex-col gap-4">
      {p.body}
      {p.footer}
    </div>
  );
}

/**
 * One question, plan or form, answerable in place, or once answered "You answered: ..." with its
 * delivery. A decided request (from the hub's recent list) renders as that line too.
 */
export function AskAnswer({ approval: a, surface, keys = false, render = stacked }: AskAnswerProps) {
  const s = useAskSend(a, surface);
  const who = useAnswerer();
  const ask = askOf(a);
  if (!ask)
    return (
      <>
        {render({
          body: <p className="text-ui text-fg-muted">Answer this in its session: this hub sent no details.</p>,
          footer: null,
        })}
      </>
    );
  if (a.state !== 'pending' || s.phase.kind !== 'idle') {
    const decided = a.state !== 'pending';
    const given = decided ? givenOf(a) : (s.phase as Exclude<SendPhase, { kind: 'idle' }>).given;
    const at = decided ? a.decidedAt : s.phase.kind === 'sent' ? s.phase.at : undefined;
    return (
      <>
        {render({
          body: <AskReceipt line={answerLine(ask, given, decided ? who(a.decidedBy) : 'You')} sending={!decided && s.phase.kind === 'sending'} at={at} />,
          footer: null,
        })}
      </>
    );
  }
  switch (ask.type) {
    case 'questions':
      return <QuestionsAnswer a={a} ask={ask} surface={surface} keys={keys} s={s} render={render} />;
    case 'plan':
      return <PlanAnswer a={a} ask={ask} surface={surface} keys={keys} s={s} render={render} />;
    case 'elicitation':
      return <ElicitationAnswer a={a} ask={ask} surface={surface} keys={keys} s={s} render={render} />;
  }
}

/** The card's heading: what Claude needs, in words. */
export function askHeading(a: Pick<ApprovalRequest, 'kind' | 'ask' | 'title'>): string {
  const ask = askOf(a);
  if (!ask) return 'Claude needs your decision';
  if (ask.type === 'questions') return ask.questions.length > 1 ? `Claude asks you ${String(ask.questions.length)} questions` : 'Claude asks you';
  if (ask.type === 'plan') return 'Claude needs your plan approval';
  return ask.mode === 'url' ? `${ask.server} needs you to open a link` : `${ask.server} needs details`;
}

/**
 * A question, plan or form as a card: the session drawer and the phone. Says what it needs, in
 * which session and since when, the controls for its kind, and where else it can be answered and
 * what happens if nobody answers. After answering it collapses to "You answered: ...".
 */
export function AskCard({ a, surface, where }: { a: ApprovalRequest; surface: 'drawer' | 'phone'; where?: string }) {
  const now = useNow(15_000);
  const session = useOrgus((st) => st.sessions[a.sessionId]);
  const titleOf = useSessionTitle();
  const sum = askSummary(a);
  const pending = a.state === 'pending';
  const Icon = pending ? Hand : KIND_ICON[askKindOf(a)];
  const heading = askHeading(a);
  const sessionTitle = session ? titleOf(session).title : null;
  return (
    <section aria-label={heading} data-testid="ask-card" className={cn('flex flex-col gap-4 rounded-card border p-4', pending ? 'border-status-attention-line bg-surface' : 'border-border bg-surface')}>
      <header className="flex min-w-0 items-start gap-3">
        <span className="flex h-5 shrink-0 items-center">
          <Icon className={cn('size-4', pending ? 'text-status-attention' : 'text-fg-subtle')} aria-hidden />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex min-w-0 items-center gap-2">
            <span className={cn('min-w-0 flex-1 truncate text-ui font-medium', pending ? 'text-status-attention-ink' : 'text-fg')}>{pending ? heading : sum.title}</span>
            {pending && <TimeLeft a={a} now={now} />}
          </span>
          <span className="truncate text-caption text-fg-subtle num">{[sessionTitle, where, `asked ${formatRelative(a.createdAt, now)}`].filter(Boolean).join('  ·  ')}</span>
        </span>
      </header>
      <AskAnswer approval={a} surface={surface} />
      {pending && <p className="text-caption text-fg-subtle">{fallbackLine(a, surface, formatClock(a.expiresAt))}</p>}
    </section>
  );
}

/**
 * A decided question, plan or form, read only (the Inbox's Done panel): what was asked and, for
 * questions, each one's answer, given in Orgus or in the session (ADR 0056).
 */
export function AskRecord({ approval: a }: { approval: ApprovalRequest }) {
  const ask = askOf(a);
  if (!ask) return null;
  const given = questionAnswersOf(a);
  if (ask.type === 'plan')
    return (
      <article aria-label="Plan" className="rounded-card bg-sunken px-6 py-4">
        <Markdown source={planBody(ask.plan) || ask.plan} />
      </article>
    );
  if (ask.type === 'elicitation') return <p className="whitespace-pre-wrap text-body text-fg">{ask.message}</p>;
  return (
    <ol className="flex flex-col gap-3">
      {ask.questions.map((q, i) => (
        <li key={i} className="flex items-start gap-2">
          {q.header && (
            <span className="flex h-5 shrink-0 items-center">
              <Badge>{q.header}</Badge>
            </span>
          )}
          <span className="flex min-w-0 flex-col gap-1">
            <span className="text-ui text-fg">{q.question}</span>
            {given?.[q.question] && (
              <span className="flex min-w-0 items-start gap-2 text-ui font-medium text-fg">
                <span className="flex h-5 shrink-0 items-center">
                  <Check className="size-3.5 text-status-success" aria-hidden />
                </span>
                <span className="min-w-0 wrap-anywhere">{given[q.question]}</span>
              </span>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}
